import { produce } from 'immer';
import { SetState, create } from 'zustand';
import { GenerateType } from '~/types';

export interface CreateNewCharacterStates {
  generateType: '' | GenerateType;
  common: {
    id: string;
    url: string;
  };
  preset: {
    id: string;
    url: string;
  };
  customizeUpload: {
    id: string;
    url: string;
    file?: File;
    uploadUrl: string;
  };
  customizeAvaturn: {
    id: string;
    url: string;
    faceImages: unknown[];
    avarturn: {
      id: string;
      url: string;
    };
    result: {
      characterId: string;
      uploadUrl: string;
    };
  };
  finalGenerateState: {
    type: GenerateType | '';
    characterId: string;
    characterName: string;
  };
  generatingState: boolean;
}

export interface CreateNewCharacterSetActions {
  setPresetState: (
    character: Pick<CreateNewCharacterStates['preset'], 'id' | 'url'>,
  ) => void;
  setGenerateType: (s: GenerateType) => void;
  setGeneratingState: (s: boolean) => void;
  setUploadFile: (s?: File) => void;
  setUserUploadUrl: (s: string) => void;
  setAvarturnState: (
    s: CreateNewCharacterStates['customizeAvaturn']['avarturn'],
  ) => void;
  setAvaturnFinalState: (
    s: CreateNewCharacterStates['customizeAvaturn']['result'],
  ) => void;
  setFinalGenerateState: ({
    type,
    characterId,
    characterName,
  }: {
    type: CreateNewCharacterStates['finalGenerateState']['type'];
    characterId?: CreateNewCharacterStates['finalGenerateState']['characterId'];
    characterName: string;
  }) => void;
}

export interface CreateNewCharacterResetActions {
  resetAll: () => void;
}

export type CreateNewCharacterActions = CreateNewCharacterSetActions &
  CreateNewCharacterResetActions;
export type CreateNewCharacterStore = CreateNewCharacterStates &
  CreateNewCharacterActions;

const initialCreateNewCharacterState = (): CreateNewCharacterStates => ({
  generateType: '',
  common: {
    id: '',
    url: '',
  },
  preset: {
    id: '',
    url: '',
  },
  customizeUpload: {
    id: '',
    url: '',
    file: undefined,
    uploadUrl: '',
  },
  customizeAvaturn: {
    id: '',
    url: '',
    faceImages: [],
    avarturn: {
      id: '',
      url: '',
    },
    result: {
      characterId: '',
      uploadUrl: '',
    },
  },
  finalGenerateState: {
    type: '',
    characterId: '',
    characterName: '',
  },
  generatingState: false,
});

const CreateNewCharacterSetActions = (
  set: SetState<CreateNewCharacterStore>,
  get: () => CreateNewCharacterStore,
): CreateNewCharacterSetActions => ({
  setPresetState: (character) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.preset.id = character.id;
        state.preset.url = character.url;
        state.common = character;
      }),
    ),
  setGenerateType: (type) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.generateType = type;
      }),
    ),
  setGeneratingState: (value) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.generatingState = value;
      }),
    ),
  setUploadFile: (file) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.customizeUpload.file = file;
      }),
    ),
  setUserUploadUrl: (s) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.customizeUpload.uploadUrl = s;
      }),
    ),
  setAvarturnState: (s) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.customizeAvaturn.avarturn = s;
      }),
    ),
  setAvaturnFinalState: (s) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.customizeAvaturn.result = s;
      }),
    ),
  setFinalGenerateState: (s) =>
    set(
      produce((state: CreateNewCharacterStates) => {
        state.finalGenerateState.type = s.type;
        if (s.characterId) state.finalGenerateState.characterId = s.characterId;
        state.finalGenerateState.characterName = s.characterName;
      }),
    ),
});

const CreateNewCharacterResetActions = (
  set: SetState<CreateNewCharacterStore>,
  get: () => CreateNewCharacterStore,
): CreateNewCharacterResetActions => ({
  resetAll: () => set(initialCreateNewCharacterState()),
});

const useCreateNewCharacterStore = create<CreateNewCharacterStore>()(
  (set, get) => ({
    ...initialCreateNewCharacterState(),
    ...CreateNewCharacterSetActions(set, get),
    ...CreateNewCharacterResetActions(set, get),
  }),
);

export default useCreateNewCharacterStore;
